// Admin API → backend /admin router (users, API keys, guardrails, model config, audit log).
import { api, qs } from "./client";
import { getHealth } from "./health";

// Users
// Paged. Returns {users, total, limit, offset} — the endpoint used to hand back
// every user on the platform, plus a GROUP BY over the whole runs table.
export const listUsers = (params) => api.get("/admin/users", { params });
// Headline counts only, no rows — for surfaces that just need the numbers.
export const getUserStats = () => api.get("/admin/users/stats");
export const createUser = (data) => api.post("/admin/users", data);
export const updateUser = (id, data) => api.put(`/admin/users/${id}`, data);
export const deleteUser = (id) => api.del(`/admin/users/${id}`);
// Password-confirmed delete — clears the user's FK references first so it can't
// silently fail on a user that owns runs/projects/notes.
export const deleteUserWithPassword = (id, password) =>
  api.post(`/admin/users/${id}/delete`, { password });
// Hard-delete every soft-removed (deleted/revoked) leftover so their unique emails are
// freed for signup again — replaces running SQL on the database by hand. Pending invites
// are never touched. Pass a company_id to limit it to one tenant.
export const purgeRemovedUsers = (password, company_id) =>
  api.post("/admin/users/purge-removed", { password, company_id });

// Guardrails
export const listGuardrails = () => api.get("/admin/guardrails");
export const createGuardrail = (data) => api.post("/admin/guardrails", data);
export const updateGuardrail = (id, data) => api.put(`/admin/guardrails/${id}`, data);
export const deleteGuardrail = (id) => api.del(`/admin/guardrails/${id}`);

// Model config
export const getModelConfig = () => api.get("/admin/model-config");
export const updateModelConfig = (data) => api.put("/admin/model-config", data);

// AI Config — both provider keys (Claude + OpenAI) and the master "Active AI"
// switch. Keys are never returned by GET — only masked previews + sources.
// updateAiConfig accepts any subset: { anthropic_api_key, openai_api_key,
// active_provider, clear_anthropic, clear_openai }.
export const getAiConfig = () => api.get("/admin/ai-config");
export const updateAiConfig = (data) => api.put("/admin/ai-config", data);
export const deleteAiConfig = () => api.del("/admin/ai-config");
export const testAiConfig = (provider = "anthropic") => api.post("/admin/ai-config/test", { provider });

// SMTP / email config — one saved config per platform, exactly one active.
// The password is never returned by GET (only a masked hint); the form sends a
// new one only when the admin types it, and blank means "keep the current one".
export const listSmtpPlatforms = () => api.get("/admin/smtp-platforms");
export const listSmtpConfigs = () => api.get("/admin/smtp-configs");
export const saveSmtpConfig = (data) => api.put("/admin/smtp-configs", data);
export const activateSmtpConfig = (id) => api.post(`/admin/smtp-configs/${id}/activate`, {});
export const deleteSmtpConfig = (id) => api.del(`/admin/smtp-configs/${id}`);
// Connects + authenticates against ONE config (even an inactive one). Passing
// `to` also sends a real email through it — the only test that proves the sender
// is authorized, not just the login.
export const testSmtpConfig = (id, to) => api.post(`/admin/smtp-configs/${id}/test`, to ? { to } : {});

// Test the sender the app is ACTUALLY using, whatever it resolves to — a stored
// config, or the .env fallback when there are no stored rows at all. That last
// case is the one testSmtpConfig can't reach, and it's the usual shape of a
// deployment where signup verification silently fails.
// Returns { sent, reason, to, host, port, from, configured, active }.
export const testLiveSender = (to) => api.post("/admin/smtp/test-live", to ? { to } : {});

// Google OAuth credentials. The client secret is never returned by GET — only a
// masked hint and whether one is set.
export const getOAuthConfig = () => api.get("/admin/oauth-config");
export const updateOAuthConfig = (data) => api.put("/admin/oauth-config", data);

// External API consumers + the keys issued to them. The full key exists in
// exactly one response: issueApiKey() / createPlatformWithKey(). It is
// unrecoverable afterwards.
// The data catalog powering the domain-grouped picker (which data a key may read).
export const listApiDomains = () => api.get("/admin/api-domains");
export const listConsumers = () => api.get("/admin/api-consumers");
export const createConsumer = (data) => api.post("/admin/api-consumers", data);
export const updateConsumer = (id, data) => api.put(`/admin/api-consumers/${id}`, data);
export const deleteConsumer = (id) => api.del(`/admin/api-consumers/${id}`);
export const listConsumerKeys = (id) => api.get(`/admin/api-consumers/${id}/keys`);
export const issueApiKey = (id, data) => api.post(`/admin/api-consumers/${id}/keys`, data);
export const revokeApiKey = (keyId) => api.post(`/admin/api-keys/${keyId}/revoke`, {});
// One-step: register a platform AND mint its first key with the selected data.
export const createPlatformWithKey = (data) => api.post("/admin/api-platforms", data);

// Companies (tenant directory)
export const listCompanies = () => api.get("/admin/companies");
export const suspendCompany = (id) => api.post(`/admin/companies/${id}/suspend`, {});

// Company detail (SuperAdmin drill-down)
export const getCompany = (id) => api.get(`/admin/companies/${id}`);
export const getCompanySubscription = (id) => api.get(`/admin/companies/${id}/subscription`);
export const getCompanyInvoices = (id) => api.get(`/admin/companies/${id}/invoices`);
export const changeCompanyPlan = (id, plan_id) => api.post(`/admin/companies/${id}/change-plan`, { plan_id });
export const cancelCompanySubscription = (id) => api.post(`/admin/companies/${id}/cancel-subscription`, {});
export const softDeleteCompany = (id) => api.del(`/admin/companies/${id}`);
export const hardDeleteCompany = (id, password) => api.post(`/admin/companies/${id}/hard-delete`, { password });

// Subscriptions (one row per tenant + its latest subscription)
export const listSubscriptions = () => api.get("/admin/subscriptions");

// Platform-wide invoices. Filtering is server-side so the table and the count
// can never disagree; `date_to` is inclusive of the whole day.
export const listInvoices = (params) => api.get("/admin/invoices", { params });
export const createInvoice = (data) => api.post("/admin/invoices", data);

// Plan catalog (editable price + limits; resolved live by all enforcement points)
export const getPlans = () => api.get("/admin/plans");
export const updatePlans = (plans) => api.put("/admin/plans", { plans });
export const createPlan = (fields) => api.post("/admin/plans", fields);
export const deletePlan = (planId) => api.del(`/admin/plans/${planId}`);

// Platform analytics (token spend aggregated across all tenants)
export const getAnalytics = (days = 30) => api.get("/admin/analytics", { params: { days } });


// --- Platform ops (maintenance, IP blocker, changelog, country pricing) ---
export const getMaintenance = () => api.get("/admin/maintenance");
export const updateMaintenance = (data) => api.put("/admin/maintenance", data);
export const listBlockedIps = () => api.get("/admin/blocked-ips");
export const blockIp = (data) => api.post("/admin/blocked-ips", data);
export const unblockIp = (id) => api.del(`/admin/blocked-ips/${id}`);
export const getLoginNotification = () => api.get("/admin/login-notification");
export const updateLoginNotification = (data) => api.put("/admin/login-notification", data);
export const getInactivityReminder = () => api.get("/admin/inactivity-reminder");
export const updateInactivityReminder = (data) => api.put("/admin/inactivity-reminder", data);
export const getCountryPricing = () => api.get("/admin/country-pricing");
export const updateCountryPricing = (pricing) => api.put("/admin/country-pricing", { pricing });
// The billing currencies the gateway settles, with their minor units and floors.
// Served rather than duplicated here: the minor-unit exponent is what turns a
// correct-looking amount into a 100x overcharge, so there is exactly one list
// and it lives in backend/core/currency.py.
export const listBillingCurrencies = () => api.get("/admin/currencies");

// Changelog / Latest Update
export const listChangelog = () => api.get("/admin/changelog");
export const createChangelog = (data) => api.post("/admin/changelog", data);
export const updateChangelogEntry = (id, data) => api.put(`/admin/changelog/${id}`, data);
export const deleteChangelogEntry = (id) => api.del(`/admin/changelog/${id}`);

// Coupons
export const listCoupons = () => api.get("/admin/coupons");
export const createCoupon = (data) => api.post("/admin/coupons", data);
export const updateCoupon = (id, data) => api.put(`/admin/coupons/${id}`, data);
export const deleteCoupon = (id) => api.del(`/admin/coupons/${id}`);

// The project's own qa-context.md — read as sections, edited one section at a time.
// Never writes to the customer's repository: the edit is an overlay this product
// composes on top of whatever the repo says (see backend/api/routers/admin_qa_context.py).
export const listQaContextProjects = () => api.get("/admin/qa-context/projects");
export const getQaContext = (projectId) => api.get(`/admin/projects/${projectId}/qa-context`);
export const saveQaContextSection = (projectId, key, data) => api.put(`/admin/projects/${projectId}/qa-context/${encodeURIComponent(key)}`, data);
export const importQaContext = (projectId, data) => api.post(`/admin/projects/${projectId}/qa-context/import`, data);
export const revertQaContextSection = (projectId, key) => api.del(`/admin/projects/${projectId}/qa-context/${encodeURIComponent(key)}`);

// Impersonate a company's owner (returns a session token for them)
export const impersonateCompany = (id) => api.post(`/admin/companies/${id}/impersonate`, {});

// Platform-wide usage report (across all tenants).
// Platform usage report. `frequency` selects the PREVIEW window only — the
// figures the page shows describe the period currently chosen in the form, so
// switching Weekly -> Monthly refetches rather than leaving last week's numbers
// under a monthly heading.
export const getPlatformUsageReport = (frequency) =>
  api.get(`/admin/usage-report${qs({ frequency })}`);
// Schedules are independent rows: creating, editing, deleting or sending one
// never touches another. A weekly digest and a monthly one can both be active.
export const createPlatformUsageSchedule = (data) =>
  api.post("/admin/usage-report/schedules", data);
export const updatePlatformUsageSchedule = (id, data) =>
  api.put(`/admin/usage-report/schedules/${id}`, data);
export const deletePlatformUsageSchedule = (id) =>
  api.del(`/admin/usage-report/schedules/${id}`);
export const sendPlatformUsageSchedule = (id) =>
  api.post(`/admin/usage-report/schedules/${id}/send`, {});

// AI Firewall — global config + custom rules (built-ins are read-only).
export const getFirewall = () => api.get("/admin/firewall");
export const updateFirewall = (data) => api.put("/admin/firewall", data);
export const createFirewallRule = (data) => api.post("/admin/firewall/rules", data);
export const updateFirewallRule = (id, data) => api.put(`/admin/firewall/rules/${id}`, data);
export const deleteFirewallRule = (id) => api.del(`/admin/firewall/rules/${id}`);
export const testFirewall = (text, target = "prompt") => api.post("/admin/firewall/test", { text, target });

// Audit log
export const listAuditLog = (params) => api.get("/admin/audit-log", { params });

export { getHealth };
